import { nomeDoProvedorOpencode } from '@remoto/shared';
import { provedorOpencodeNaoTratado, type ProvedorOpencode } from '@remoto/protocolo';
import { IconeForaDeCasa } from './icones';

/**
 * Para onde a conversa vai, por extenso, para o `title` do selo.
 *
 * Por provedor (F2 da revisão de out/2026, D13 e D41), porque a promessa de
 * privacidade NÃO é a mesma nos dois: o OpenRouter tem o `data_collection:
 * deny` por chamada; o NanoGPT não tem equivalente garantido, e vale a política
 * geral dele. Prometer no NanoGPT o que só o OpenRouter dá seria o selo
 * mentindo justamente sobre o que ele existe para avisar.
 *
 * Sem provedor conhecido (o catálogo ainda não chegou), a frase não nomeia
 * ninguém — dizer um nome ali seria chute.
 */
export function explicacaoDoSelo(provedor: ProvedorOpencode | null): string {
  if (provedor === null) {
    return 'O conteúdo da conversa vai ao provedor de modelos da frota e ao provedor do modelo, fora da rede de casa.';
  }
  switch (provedor) {
    case 'nanogpt':
      return 'O conteúdo da conversa vai ao NanoGPT e ao provedor do modelo, fora da rede de casa. O NanoGPT diz não guardar nem treinar com o conteúdo, mas não há garantia por chamada.';
    case 'openrouter':
      return 'O conteúdo da conversa vai ao OpenRouter e ao provedor do modelo, fora da rede de casa. Só provedores que não guardam dados são usados.';
    default:
      return provedorOpencodeNaoTratado(provedor);
  }
}

/**
 * O selo "sai da rede de casa · <provedor>" (F17, D11, ADR-0022; F2: o
 * provedor ATIVO, NanoGPT ou OpenRouter).
 *
 * A regra da casa é que o conteúdo das conversas não sai da rede de casa. O
 * OpenCode é a exceção escolhida pelo dono: cada turno vai ao provedor de
 * modelos da frota e ao provedor do modelo — e, com a web ligada, à Exa. Uma
 * exceção que não aparece na tela vira regra esquecida, e por isso o selo
 * mora onde a decisão é tomada: na tela de Configurações, na criação da sessão
 * e na conversa.
 *
 * Um componente só, e não o texto repetido em cada tela, para a frase não
 * divergir entre três lugares na primeira revisão de texto. Quem desenha diz o
 * provedor que SABE: a configuração na tela dela, o catálogo na de criar, o
 * modelo da conversa no cabeçalho. `null` é "não sei", e o selo fica sem nome.
 *
 * A explicação longa vai no `title`: o selo é curto para caber numa linha de
 * formulário no telefone, e quem quer saber para onde vai passa o mouse.
 */
export function SeloForaDeCasa({
  className,
  provedor = null,
}: { className?: string; provedor?: ProvedorOpencode | null } = {}): React.JSX.Element {
  return (
    <span
      data-testid="selo-fora-de-casa"
      data-provedor={provedor ?? undefined}
      title={explicacaoDoSelo(provedor)}
      className={
        'inline-flex max-w-full items-center gap-1 rounded-full border border-amber-700/60 bg-amber-950/40 px-2 py-0.5 text-[11px] text-amber-200 ' +
        (className ?? '')
      }
    >
      <IconeForaDeCasa tamanho={12} />
      <span className="truncate">
        sai da rede de casa{provedor === null ? '' : ' · ' + nomeDoProvedorOpencode(provedor)}
      </span>
    </span>
  );
}
